/**
 * Composable for fetching and accessing product custom fields
 *
 * Shopware 6.5+ doesn't include product custom fields in cart/order line item payloads.
 * This composable provides a way to fetch and cache custom fields for products.
 */

import type { ProductCustomFields } from '~/lib/utils/shopware'

interface LineItemLike {
  type?: string
  referencedId?: string
}

/**
 * Extract product IDs from line items (cart items or order line items)
 */
export const getProductIdsFromLineItems = (lineItems: LineItemLike[]): string[] => {
  return lineItems
    .filter((item) => item.type === 'product' && item.referencedId)
    .map((item) => item.referencedId as string)
}

export const useProductCustomFields = () => {
  const { apiClient } = useShopwareContext()

  // Cache for product custom fields
  const customFieldsMap = ref<Map<string, ProductCustomFields | null>>(new Map())

  /**
   * Fetch custom fields for multiple products by their IDs
   */
  const fetchCustomFields = async (productIds: string[]) => {
    // Filter out IDs we already have cached
    const idsToFetch = productIds.filter((id) => !customFieldsMap.value.has(id))

    if (idsToFetch.length === 0) return

    try {
      const response = await apiClient.invoke('readProduct post /product', {
        body: {
          ids: idsToFetch,
        },
      })

      const products = (response.data?.elements || []) as Array<Record<string, unknown>>
      products.forEach((product) => {
        const id = product.id as string
        const translated = product.translated as Record<string, unknown> | undefined
        const customFields = (translated?.customFields || product.customFields) as ProductCustomFields | null
        customFieldsMap.value.set(id, customFields)
      })
    } catch (error) {
      console.error('Failed to fetch product custom fields:', error)
    }
  }

  /**
   * Get custom fields for a product by ID
   */
  const getCustomFields = (productId: string | undefined): ProductCustomFields | null => {
    if (!productId) return null
    return customFieldsMap.value.get(productId) || null
  }

  /**
   * Check if a product is marked as "soon" (not yet available)
   */
  const isProductSoon = (productId: string | undefined): boolean => {
    const customFields = getCustomFields(productId)
    return Boolean(customFields?.custom_product_fields_soon)
  }

  /**
   * Clear the cache (useful when language changes)
   */
  const clearCache = () => {
    customFieldsMap.value.clear()
  }

  return {
    customFieldsMap,
    fetchCustomFields,
    getCustomFields,
    isProductSoon,
    clearCache,
  }
}
